import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Inbox, Loader2, Mail, Phone, Save } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/landing/Reveal";
import { api, formatApiError, RISK } from "@/lib/api";
import { AdminClients } from "@/components/AdminClients";
import { AdminAccessRequests } from "@/components/AdminAccessRequests";

export const STATUSES = {
  nuevo: { label: "Nuevo", cls: "text-[#F59E0B] border-amber-500/40 bg-amber-500/10" },
  contactado: { label: "Contactado", cls: "text-sky-300 border-sky-400/40 bg-sky-400/10" },
  en_proceso: { label: "En proceso", cls: "text-violet-300 border-violet-400/40 bg-violet-400/10" },
  cerrado: { label: "Cerrado", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
};

const fmt = (d) => new Date(d).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const Detail = ({ req, onSaved }) => {
  const [status, setStatus] = useState(req.status);
  const [notes, setNotes] = useState(req.notes || "");
  const [busy, setBusy] = useState(false);
  const r = req.audit ? RISK[req.audit.risk] ?? RISK.Alto : null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/consultant-requests/${req.id}`, { status, notes });
      onSaved({ ...req, ...data });
      toast.success("Seguimiento actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div key={req.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="border border-white/10 bg-[#111722] p-6 sm:p-8" data-testid="request-detail">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F1E8]/55">Recibida {fmt(req.created_at)}</div>
      <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight" data-testid="request-detail-name">{req.name}</h2>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#F59E0B]" /> {req.company}</div>
        <a href={`mailto:${req.email}`} data-testid="request-detail-email" className="flex items-center gap-2 hover:text-[#F59E0B] transition-colors"><Mail className="w-4 h-4 text-[#F59E0B]" /> {req.email}</a>
        <a href={`tel:${req.phone}`} data-testid="request-detail-phone" className="flex items-center gap-2 hover:text-[#F59E0B] transition-colors"><Phone className="w-4 h-4 text-[#F59E0B]" /> {req.phone}</a>
      </div>
      {req.message && <p className="mt-5 border-l-2 border-[#F59E0B] pl-4 text-[#F5F1E8]/80" data-testid="request-detail-message">“{req.message}”</p>}

      {req.audit && (
        <Link to={`/resumen/${req.audit_id}`} data-testid="request-audit-link" className="group mt-6 flex items-center justify-between gap-4 border border-white/10 p-4 hover:border-[#F59E0B] transition-colors">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/55">Auditoría vinculada · {req.audit.module_name} · {req.audit.site}</div>
            <div className="mt-1 flex items-center gap-3">
              <span className="font-display-x text-2xl font-black">{req.audit.score}%</span>
              <span className={`px-2.5 py-0.5 rounded-full border text-xs font-semibold ${r.bg} ${r.border} ${r.text}`}>{r.label}</span>
              <span className="text-xs text-[#F5F1E8]/60">{req.audit.no} hallazgos</span>
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 transition-transform group-hover:rotate-45" />
        </Link>
      )}

      <div className="mt-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">Estado</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(STATUSES).map(([k, s]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`status-option-${k}`} aria-pressed={status === k}
              className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${status === k ? s.cls : "border-white/15 text-[#F5F1E8]/70 hover:border-white/40"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <label className="block mt-6">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">Notas de seguimiento</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="request-notes-input" data-lenis-prevent
          placeholder="Ej. Llamada agendada el jueves; enviar propuesta NOM-002." className="mt-2 w-full bg-transparent border border-white/15 p-3 outline-none focus:border-[#F59E0B] transition-colors resize-none" />
      </label>
      <button onClick={save} disabled={busy} data-testid="request-save-button" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] font-semibold hover:bg-[#F5F1E8] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar seguimiento
      </button>
      {req.updated_at && <p className="mt-3 font-mono text-xs text-[#F5F1E8]/50">Última actualización {fmt(req.updated_at)}</p>}
    </motion.div>
  );
};

export default function AdminInbox() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("solicitudes");
  const [access, setAccess] = useState(null);
  const loadAccess = useCallback(() => {
    api.get("/admin/premium-requests").then((r) => setAccess(r.data)).catch(() => setAccess({ unseen: 0, items: [] }));
  }, []);
  useEffect(() => { loadAccess(); }, [loadAccess]);
  const markSeen = useCallback(() => setAccess((a) => (a ? { ...a, unseen: 0 } : a)), []);

  useEffect(() => {
    api.get("/consultant-requests").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const counts = useMemo(() => (items ?? []).reduce((m, i) => ({ ...m, [i.status]: (m[i.status] || 0) + 1 }), {}), [items]);
  const list = (items ?? []).filter((i) => filter === "todos" || i.status === filter);
  const current = items?.find((i) => i.id === selected) ?? null;
  const onSaved = (u) => setItems((all) => all.map((i) => (i.id === u.id ? u : i)));

  return (
    <main className="min-h-screen" data-testid="admin-inbox-page">
      <Navbar variant="app" />
      <div className="mx-auto max-w-[1300px] px-5 sm:px-8 pt-32 pb-24">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Ascentra · Consultores</div>
        <h1 className="font-display-x mt-5 text-4xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tight"><MaskLines lines={["Panel de", "consultores"]} /></h1>

        <div className="mt-10 flex gap-6 border-b border-white/10 overflow-x-auto" role="tablist" data-testid="admin-view-tabs">
          {[["solicitudes", "Solicitudes"], ["acceso", "Solicitudes de acceso prémium"], ["clientes", "Clientes y planes"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`admin-view-${k}`}
              className={`relative shrink-0 pb-3 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${view === k ? "text-[#F59E0B]" : "text-[#F5F1E8]/70 hover:text-white"}`}>
              {l}
              {k === "acceso" && access?.unseen > 0 && (
                <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[#F59E0B] text-[#0B0F17] font-mono text-[11px] font-bold" data-testid="access-requests-counter">{access.unseen}</motion.span>
              )}
              {view === k && <motion.span layoutId="admin-tab" className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#F59E0B]" />}
            </button>
          ))}
        </div>

        {view === "acceso" ? <AdminAccessRequests data={access} onSeen={markSeen} /> : view === "clientes" ? <AdminClients /> : (<>

        <div className="mt-12 flex gap-2 overflow-x-auto pb-2" data-testid="inbox-filters">
          {[["todos", "Todas", items?.length ?? 0], ...Object.entries(STATUSES).map(([k, s]) => [k, s.label, counts[k] || 0])].map(([k, label, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${filter === k ? "bg-[#F5F1E8] text-[#0B0F17] border-[#F5F1E8]" : "border-white/15 text-[#F5F1E8]/75 hover:border-white/40"}`}>
              {label} <span className="font-mono text-xs opacity-70">{n}</span>
            </button>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6 min-w-0 border-t border-white/10" data-testid="inbox-list">
            {items === null && <div className="py-12 font-mono text-sm text-[#F5F1E8]/60">Cargando…</div>}
            {items !== null && list.length === 0 && (
              <div className="py-16 flex items-center gap-3 text-[#F5F1E8]/65" data-testid="inbox-empty"><Inbox className="w-6 h-6" /> No hay solicitudes en esta vista.</div>
            )}
            {list.map((i) => {
              const s = STATUSES[i.status] ?? STATUSES.nuevo;
              return (
                <button key={i.id} onClick={() => setSelected(i.id)} data-testid={`inbox-item-${i.id}`}
                  className={`w-full text-left grid grid-cols-12 gap-3 items-center py-5 px-3 border-b border-white/10 transition-colors ${selected === i.id ? "bg-white/[0.05]" : "hover:bg-white/[0.02]"}`}>
                  <div className="col-span-8 min-w-0">
                    <div className="font-semibold truncate">{i.name} <span className="text-[#F5F1E8]/55 font-normal">· {i.company}</span></div>
                    <div className="mt-1 font-mono text-xs text-[#F5F1E8]/55">{fmt(i.created_at)}{i.audit ? ` · ${i.audit.score}% · riesgo ${i.audit.risk.toLowerCase()}` : ""}</div>
                  </div>
                  <div className="col-span-4 flex justify-end"><span className={`px-3 py-1 rounded-full border text-xs font-semibold ${s.cls}`}>{s.label}</span></div>
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-6 min-w-0">
            <div className="lg:sticky lg:top-28">
              {current ? <Detail key={current.id} req={current} onSaved={onSaved} /> : (
                <div className="border border-dashed border-white/15 p-10 text-[#F5F1E8]/60" data-testid="inbox-no-selection">Selecciona una solicitud para ver el detalle y registrar el seguimiento.</div>
              )}
            </div>
          </div>
        </div>
        </>)}
      </div>
    </main>
  );
}
